export const STORYTELLING_FONT_FAMILY_KEYS = [
  "sans",
  "serif",
  "display",
  "rounded",
  "mono",
  "script",
  "condensed",
  "geometric",
  "slab",
  "elegant",
  "handwritten",
  "impact",
  "vintage",
  "casual",
];

export const STORYTELLING_FONT_FAMILIES = {
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", "Palatino Linotype", serif',
  display: '"Playfair Display", "Cormorant Garamond", Georgia, serif',
  rounded: '"Nunito", "Varela Round", "Segoe UI Rounded", sans-serif',
  mono: '"Courier New", Courier, "Lucida Console", monospace',
  script: '"Segoe Script", "Brush Script MT", "Lucida Handwriting", cursive',
  condensed:
    '"Arial Narrow", "Helvetica Neue Condensed", "Franklin Gothic Medium", sans-serif',
  geometric: '"Avenir Next", Futura, "Century Gothic", sans-serif',
  slab: '"Rockwell", "Roboto Slab", Georgia, serif',
  elegant: '"Didot", "Bodoni MT", "Times New Roman", serif',
  handwritten: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", cursive',
  impact: 'Impact, "Arial Black", "Haettenschweiler", sans-serif',
  vintage: '"American Typewriter", "Courier New", Georgia, serif',
  casual: '"Trebuchet MS", "Lucida Grande", "Gill Sans", sans-serif',
};

export const STORYTELLING_FONT_SIZE_KEYS = ["small", "medium", "large"];

export const STORYTELLING_TYPOGRAPHY_SIZE_SCALES = {
  name: {
    small: { client: "2.25rem", preview: "0.95rem" },
    medium: { client: "2.85rem", preview: "1.1rem" },
    large: { client: "3.5rem", preview: "1.25rem" },
  },
  question: {
    small: { client: "1.2rem", preview: "0.82rem" },
    medium: { client: "1.5rem", preview: "0.95rem" },
    large: { client: "1.85rem", preview: "1.1rem" },
  },
  description: {
    small: { client: "1rem", preview: "0.72rem" },
    medium: { client: "1.15rem", preview: "0.82rem" },
    large: { client: "1.3rem", preview: "0.95rem" },
  },
};

export const STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS = {
  name: "#000000",
  question: "#000000",
  description: "#475569",
};

export const STORYTELLING_TYPOGRAPHY_DEFAULT_FAMILIES = {
  name: "display",
  question: "display",
  description: "sans",
};

export const STORYTELLING_TYPOGRAPHY_DEFAULT_SIZES = {
  name: "large",
  question: "medium",
  description: "medium",
};

export function isStorytellingFontFamily(value) {
  return STORYTELLING_FONT_FAMILY_KEYS.includes(value);
}

export function isStorytellingFontSize(value) {
  return STORYTELLING_FONT_SIZE_KEYS.includes(value);
}

export function getStorytellingFontOptions(t) {
  return STORYTELLING_FONT_FAMILY_KEYS.map((key) => ({
    value: key,
    label: t(`storytellingFont_${key}`),
    fontFamily: STORYTELLING_FONT_FAMILIES[key],
  }));
}

export function getStorytellingTypographyStyle(display, role, variant = "client") {
  const familyKey = display?.[`${role}FontFamily`] || STORYTELLING_TYPOGRAPHY_DEFAULT_FAMILIES[role];
  const sizeKey = display?.[`${role}FontSize`] || STORYTELLING_TYPOGRAPHY_DEFAULT_SIZES[role];
  const color =
    display?.[`${role}Color`] || STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS[role];
  const scale = STORYTELLING_TYPOGRAPHY_SIZE_SCALES[role];
  const sizes = scale?.[sizeKey] || scale?.medium;

  const boldFamilies = new Set(["display", "elegant", "impact", "slab"]);

  return {
    fontFamily: STORYTELLING_FONT_FAMILIES[familyKey] || STORYTELLING_FONT_FAMILIES.sans,
    fontSize: sizes[variant] || sizes.client,
    color,
    fontWeight: boldFamilies.has(familyKey) ? 700 : role === "description" ? 400 : 600,
    lineHeight: role === "description" ? 1.55 : 1.3,
    margin: 0,
    whiteSpace: role === "description" ? "pre-wrap" : undefined,
  };
}

export function getStorytellingNameStyle(display, variant = "client") {
  const { color: _color, ...rest } = getStorytellingTypographyStyle(display, "name", variant);
  return rest;
}

export function getStorytellingNamePartColors(display) {
  return {
    primary: display.nameColor,
    accent: display.nameAccentColor || display.accentColor,
  };
}

export function getStorytellingQuestionStyle(display, variant = "client") {
  return getStorytellingTypographyStyle(display, "question", variant);
}

export function getStorytellingDescriptionStyle(display, variant = "client") {
  return getStorytellingTypographyStyle(display, "description", variant);
}
